/**
 * Holds the access token in memory for the lifetime of the page.
 *
 * Deliberately NOT localStorage or sessionStorage. Anything readable by script is
 * readable by an XSS payload, so the long-lived credential — the refresh token —
 * lives in an HttpOnly cookie the browser will not expose to JavaScript at all.
 * What is kept here is a 15-minute access token, which caps the value of stealing
 * it.
 *
 * The cost is that a page reload loses the token. That is paid back by a single
 * silent refresh at boot, which is what `AuthProvider` does before rendering.
 *
 * A module-level variable rather than React state, because the Axios interceptor
 * needs the token SYNCHRONOUSLY from outside the component tree.
 */

let accessToken: string | null = null;

/** Called whenever the token changes, so React can re-render on sign-out. */
type Subscriber = (token: string | null) => void;

const subscribers = new Set<Subscriber>();

export const tokenStore = {
  /** The current access token, or null when signed out. */
  get(): string | null {
    return accessToken;
  },

  /** Replaces the token and notifies subscribers. */
  set(token: string | null): void {
    accessToken = token;
    subscribers.forEach((notify) => notify(token));
  },

  /** Clears the token. */
  clear(): void {
    this.set(null);
  },

  /** Subscribes to token changes. Returns an unsubscribe function. */
  subscribe(subscriber: Subscriber): () => void {
    subscribers.add(subscriber);
    return () => subscribers.delete(subscriber);
  },
};
